import { PaymentCardInformation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentCardInformation'
import { type Dispatch, type FunctionComponent, useEffect, useState } from 'react'
import { ScrollView } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import { Skeleton } from '@/src/design-system/Skeleton'
import { LegacyAutoPaySchedule } from '@/src/experiences/card-payments/screens/AutoPaySchedule/LegacyAutoPaySchedule'
import { useEvent } from 'tamagui'
import { PlatformAutoPaySchedule } from '@/src/experiences/card-payments/screens/AutoPaySchedule/PlatformAutoPaySchedule'

export function useAutoPayCardStackIndexScreenProps() {
  return usePaymentScreen('autoPay/index', 'Select Auto-pay', '/main/card/card-management/payment')
}

export type AutoPayScheduleComponentType = <PaymentStrategy, Day>(props: {
  paymentStrategies?: PaymentStrategy[]
  paymentStrategySelector?: React.ReactNode
  daySelector?: React.ReactNode
  paymentStrategy?: PaymentStrategy
  setPaymentStrategy?: Dispatch<ReturnType<typeof useState<PaymentStrategy>>[0]>
  day?: Day
  setDay?: Dispatch<ReturnType<typeof useState<Day>>[0]>
  recurringPaymentStore?: Pick<
    typeof props,
    'paymentStrategy' | 'setPaymentStrategy' | 'day' | 'setDay'
  >
  shouldDisableContinueButton?: boolean
}) => ReturnType<FunctionComponent>

const AutoPaySchedule = provideCardPlatform<AutoPayScheduleComponentType>(
  ({ platform, ToWrap }) => {
    switch (platform) {
      case 'HIGHNOTE':
        return <LegacyAutoPaySchedule AutoPayScheduleComponent={ToWrap} />
      case 'LOANPRO':
        return <PlatformAutoPaySchedule AutoPayScheduleComponent={ToWrap} />
      default:
        platform satisfies undefined
        return <ToWrap />
    }
  },
)(({
  recurringPaymentStore,
  paymentStrategies,
  paymentStrategySelector,
  paymentStrategy,
  setPaymentStrategy,
  daySelector,
  day,
  setDay,
  shouldDisableContinueButton,
}) => {
  const selectValuesOnInitialRender = useEvent(() => {
    recurringPaymentStore?.paymentStrategy != null
      ? setPaymentStrategy?.(recurringPaymentStore.paymentStrategy)
      : setPaymentStrategy?.(paymentStrategies?.[0])
    recurringPaymentStore?.day != null ? setDay?.(recurringPaymentStore.day) : setDay?.(undefined)
  })

  const { Modal, routerPush } = useAutoPayCardStackIndexScreenProps()

  const onContinuePress = () => {
    recurringPaymentStore?.setPaymentStrategy?.(paymentStrategy)
    recurringPaymentStore?.setDay?.(day)
    routerPush('/main/(tabs)/card/card-management/payment/autoPay/fundingSource')
  }

  useEffect(() => selectValuesOnInitialRender(), [selectValuesOnInitialRender])

  return Modal({
    children: (
      <ScrollView>
        {paymentStrategySelector || <Skeleton style={{ height: 196 }} />}
        <PaymentCardInformation title="Date" containerStyle={{ margin: 16 }}>
          {daySelector || <Skeleton style={{ height: 48 }} />}
        </PaymentCardInformation>
        <Button
          onPress={onContinuePress}
          testID={CardPaymentsE2E.Ids.Press.Continue}
          title="Continue"
          style={{ marginBottom: 16 }}
          disabled={!paymentStrategy || !day || shouldDisableContinueButton}
        />
      </ScrollView>
    ),
  })
})

export const AutoPayScheduleScreen = {
  name: 'auto-pay-schedule',
  Screen: AutoPaySchedule,
} as const
